import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PinFixedFill from '@icons/PinFixedFill';
import { Link } from '@stackflow/link';
import { pressActionEffect } from '@styles/mixin';
import { ComponentProps, ReactNode } from 'react';

type LinkProps = ComponentProps<typeof Link>;

interface Props {
    activityName: LinkProps['activityName'];
    activityParams: LinkProps['activityParams'];
    /** 상단 좌측 뱃지 (이벤트 상태 등) */
    badge?: ReactNode;
    /** 상단 좌측 기간·작성일 */
    meta: string;
    isPinned?: boolean;
    title: string;
    imageUrl?: string | null;
    imageAlt?: string;
    /** 한 줄 요약. HTML 이면 summaryHtml 을 쓴다 */
    summary?: string;
    summaryHtml?: string;
}

/** 새소식(이벤트·공지사항) 목록 카드 */
function BoardCard({
    activityName, activityParams, badge, meta, isPinned, title,
    imageUrl, imageAlt, summary, summaryHtml,
}: Props) {
    return (
        <Link activityName={activityName}
            activityParams={activityParams}
            css={cardCss} >
            <Row fullWidth justify='space-between'>
                <Row spacing={2}>
                    {badge}
                    <Text size='body-long' color={colors.textSecondary}>{meta}</Text>
                </Row>
                {isPinned && (
                    <PinFixedFill size={20} color={colors.textSecondary}/>
                )}
            </Row>
            <Title size='title' fontWeight='medium' color={colors.textPrimary}>
                {title}
            </Title>
            {imageUrl && (
                <Thumbnail src={imageUrl} alt={imageAlt || title}/>
            )}
            {summaryHtml ? (
                <Summary color={colors.textSecondary}
                    as='div'
                    dangerouslySetInnerHTML={{ __html: summaryHtml }}/>
            ) : summary && (
                <Summary color={colors.textSecondary}>{summary}</Summary>
            )}
        </Link>
    );
}

export const cardCss = css`
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: var(--spacing-4);
    row-gap: var(--spacing-4);
    width: 100%;
    ${pressActionEffect}
`;

const Title = styled(Text)`
    -webkit-box-orient: vertical;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-word;
    width: 100%;
`;

const Thumbnail = styled(Image)`
    border-radius: var(--radius-xs);
    height: 172px;
    object-fit: cover;
    width: 100%;
`;

const Summary = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;

export const BoardCardList = styled(Column)`
    padding: var(--spacing-4);
`;

export default BoardCard;
